import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { CreditCard, Plus, Trash2, Loader2, ShieldCheck, ShieldAlert, X } from "lucide-react";

const VISA = "https://images.unsplash.com/photo-1648161235879-d2ba122f9ad9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHx2aXNhJTIwbWFzdGVyY2FyZCUyMGNyZWRpdCUyMGNhcmQlMjBsb2dvfGVufDB8fHx8MTc4MDM3NTM1M3ww&ixlib=rb-4.1.0&q=85";

export default function Cards() {
  const [cards, setCards] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ card_number: "", expiry: "", cvv: "", card_holder: "", nickname: "" });
  const [loading, setLoading] = useState(false);
  const [verifyCardId, setVerifyCardId] = useState(null);
  const [paypalClientId, setPaypalClientId] = useState("");

  const load = async () => {
    const { data } = await api.get("/wallet/cards");
    setCards(data.cards || []);
  };

  useEffect(() => {
    load();
    api.get("/config/paypal").then(({ data }) => setPaypalClientId(data.client_id || "")).catch(() => {});
  }, []);

  const addCard = async () => {
    const clean = form.card_number.replace(/\s/g, "");
    if (!/^\d{12,19}$/.test(clean)) { toast.error("Enter a valid card number."); return; }
    if (!/^\d{2}\/\d{2}$/.test(form.expiry)) { toast.error("Expiry must be MM/YY."); return; }
    if (!/^\d{3,4}$/.test(form.cvv)) { toast.error("CVV must be 3 or 4 digits."); return; }
    if (!form.card_holder) { toast.error("Enter the card holder name."); return; }
    setLoading(true);
    try {
      await api.post("/wallet/cards", { ...form, card_number: clean });
      toast.success("Card added.");
      setForm({ card_number: "", expiry: "", cvv: "", card_holder: "", nickname: "" });
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const deleteCard = async (id) => {
    try {
      await api.delete(`/wallet/cards/${id}`);
      toast.success("Card removed.");
      setCards(cards.filter((c) => c.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-eyebrow text-[#4F4D45]">Saved methods</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Card details</h1>
          <p className="text-[#4F4D45] mt-2 max-w-xl">We store only your card brand, holder name, and last 4 digits — never the full PAN or CVV.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1B3526] hover:bg-[#0F2517]" data-testid="cards-add-button">
              <Plus className="w-4 h-4 mr-2" /> Add card
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add a card</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-eyebrow text-[#4F4D45]">Card number</Label>
                <Input value={form.card_number} onChange={(e) => setForm({ ...form, card_number: e.target.value.replace(/[^\d ]/g, "") })} placeholder="1234 5678 9012 3456" data-testid="cards-input-number" className="mt-1 font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-eyebrow text-[#4F4D45]">Expiry</Label>
                  <Input value={form.expiry} onChange={(e) => setForm({ ...form, expiry: e.target.value })} placeholder="MM/YY" data-testid="cards-input-expiry" className="mt-1" />
                </div>
                <div>
                  <Label className="text-eyebrow text-[#4F4D45]">CVV</Label>
                  <Input value={form.cvv} onChange={(e) => setForm({ ...form, cvv: e.target.value.replace(/\D/g, "") })} placeholder="123" data-testid="cards-input-cvv" className="mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-eyebrow text-[#4F4D45]">Card holder</Label>
                <Input value={form.card_holder} onChange={(e) => setForm({ ...form, card_holder: e.target.value })} placeholder="JORDAN RIVERA" data-testid="cards-input-holder" className="mt-1" />
              </div>
              <div>
                <Label className="text-eyebrow text-[#4F4D45]">Nickname (optional)</Label>
                <Input value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} placeholder="Travel card" data-testid="cards-input-nickname" className="mt-1" />
              </div>
              <div className="flex items-start gap-2 text-xs text-[#4F4D45] pt-2">
                <ShieldCheck className="w-4 h-4 text-[#1B3526] shrink-0 mt-0.5" />
                <span>The number & CVV are not stored. Only brand, holder and last 4 are kept for your wallet.</span>
              </div>
              <Button onClick={addCard} disabled={loading} className="bg-[#1B3526] hover:bg-[#0F2517] w-full" data-testid="cards-save-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save card"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="cards-list">
        {cards.length === 0 && (
          <div className="col-span-full fv-card p-10 text-center">
            <img src={VISA} alt="Card" className="w-40 mx-auto rounded-xl shadow-sm" />
            <h3 className="font-display text-xl font-medium mt-4">No cards yet</h3>
            <p className="text-[#4F4D45] text-sm mt-1">Add a card to keep track of your funding methods.</p>
          </div>
        )}
        {cards.map((c) => (
          <div key={c.id} className="fv-card p-6 relative overflow-hidden bg-gradient-to-br from-[#1B3526] to-[#264C39] text-white" data-testid={`card-item-${c.id}`}>
            <div className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
            <div className="flex items-start justify-between">
              <CreditCard className="w-6 h-6" strokeWidth={1.5} />
              <span className="text-xs text-white/70">{c.brand}</span>
            </div>
            <div className="mt-8 font-mono text-lg tracking-[0.3em]">•••• •••• •••• {c.last4}</div>
            <div className="mt-6 flex items-end justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/60">Holder</div>
                <div className="text-sm">{c.card_holder}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/60">Expires</div>
                <div className="text-sm">{c.expiry}</div>
              </div>
            </div>
            {c.nickname && <div className="absolute top-6 left-1/2 -translate-x-1/2 text-xs text-white/70">{c.nickname}</div>}
            <button onClick={() => deleteCard(c.id)} data-testid={`card-delete-${c.id}`} className="absolute top-4 right-4 text-white/60 hover:text-white p-1.5 rounded-md hover:bg-white/10">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </AppLayout>
  );
}
